/**
 * ConfirmDeleteModal — Modal de confirmación irreversible.
 *
 * Requiere que el usuario escriba la palabra "ELIMINAR" para habilitar el botón.
 * Uso:
 *   const [target, setTarget] = useState(null);
 *   <ConfirmDeleteModal
 *     open={!!target}
 *     onCancel={() => setTarget(null)}
 *     onConfirm={async () => { await api.delete(...); setTarget(null); await load(); }}
 *     title="Eliminar boletín"
 *     description="Se eliminará <b>Boletín #12</b>. Esta acción es irreversible."
 *   />
 */
import React, { useEffect, useState } from "react";
import { IconAlertTriangle, IconLoader2, IconTrash, IconX } from "@tabler/icons-react";

const REQUIRED_WORD = "ELIMINAR";

export default function ConfirmDeleteModal({
  open,
  onCancel,
  onConfirm,
  title = "Eliminar",
  description = "Esta acción es irreversible.",
  confirmLabel = "Eliminar",
  cancelLabel = "Cancelar",
  requiredWord = REQUIRED_WORD,
  testId = "confirm-delete-modal",
}) {
  const [typed, setTyped] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setTyped("");
      setError("");
      setLoading(false);
    }
  }, [open]);

  if (!open) return null;

  const canConfirm = typed.trim().toUpperCase() === requiredWord && !loading;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setLoading(true);
    setError("");
    try {
      await onConfirm();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "No se pudo eliminar. Intentá de nuevo.";
      setError(typeof msg === "string" ? msg : "No se pudo eliminar.");
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
      onClick={() => !loading && onCancel && onCancel()}
      data-testid={testId}
    >
      <div
        className="bg-paper rounded-lg max-w-md w-full p-6 shadow-xl border border-line"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="shrink-0 w-9 h-9 rounded-full bg-errbg flex items-center justify-center">
            <IconAlertTriangle size={18} className="text-errtext" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-[17px] font-medium leading-snug">{title}</h3>
            <p
              className="text-[13px] text-muted mt-1 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
          <button
            type="button"
            className="text-tert hover:text-ink"
            onClick={() => !loading && onCancel && onCancel()}
            aria-label="Cerrar"
            data-testid={`${testId}-close`}
          >
            <IconX size={16} />
          </button>
        </div>

        <div className="field mb-3">
          <label className="lbl">
            Para confirmar, escribí <span className="font-mono font-semibold text-errtext">{requiredWord}</span>
          </label>
          <input
            className="input font-mono tracking-wide"
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={requiredWord}
            disabled={loading}
            data-testid={`${testId}-input`}
          />
        </div>

        {error && (
          <p className="text-[12.5px] text-errtext bg-errbg rounded-md px-3 py-2 mb-3" data-testid={`${testId}-error`}>
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-ghost text-[13px]"
            onClick={() => !loading && onCancel && onCancel()}
            disabled={loading}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn text-[13px] ${canConfirm ? "bg-errtext text-white border-errtext" : "opacity-50 cursor-not-allowed"}`}
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid={`${testId}-confirm`}
          >
            {loading ? <IconLoader2 size={13} className="animate-spin" /> : <IconTrash size={13} />}
            {loading ? "Eliminando…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
